import React, { useEffect, useState } from 'react';
import { ThemeProvider, Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { theme } from '../../Medical_History_Forms/themeprovider';
import { convertUnderscoreToTitle, infoTextStyle, sectionTitleStyle } from '../../../utils/commonUtils';
import { useLoader } from '../../loaderProvider/loader';
import styles from '../oocyteRetrieval/oocyteRetrival.module.css';
import { getHysteroscopy, HysteroscopyType } from './hysteroscopy.helper';
import ImageLightbox from '../ImageLightbox';

interface LaparoscopyViewDetailsProps {
  record: number;
  onBack: () => void;
  onEdit?: () => void;
}

const HysteroscopyViewDetails: React.FC<LaparoscopyViewDetailsProps> = ({ record, onBack, onEdit }) => {
  const { showLoader, hideLoader } = useLoader();
  const [retrievalData, setRetrievalData] = useState<HysteroscopyType | null>(null);

  const [openImageDialog, setOpenImageDialog] = useState(false); 
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  const handleOpenDialog = (index: number) => {
    if (!retrievalData?.images) return;
    const urls = retrievalData.images.map(img => img.image);
    setSelectedImages(urls);
    setSelectedImageIndex(index);
    setOpenImageDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenImageDialog(false);
    setSelectedImages([]);
    setSelectedImageIndex(0);
  };
  
  useEffect(() => {
    if (!record) return;

    showLoader();
    getHysteroscopy(record)
      .then((data) => setRetrievalData(data))
      .catch(() => {
        setRetrievalData(null);
      })
      .finally(() => hideLoader());
  }, [record, showLoader, hideLoader]);

  const fields = [
    { label: 'Date', value: retrievalData?.date },
    { label: 'Name of Surgeon', value: retrievalData?.surgeon_name },
    { label: 'Assistant Surgeon', value: retrievalData?.assistant_surgeon_name },
    { label: 'Anesthetist', value: retrievalData?.anesthetist_name },
    { label: 'Procedure Done', value: convertUnderscoreToTitle(retrievalData?.procedure_done) },
    { label: 'Duration of Procedure', value: retrievalData?.procedure_duration },
    { label: 'Cervical Os', value: convertUnderscoreToTitle(retrievalData?.cervical_os) },
    { label: 'Endocervical Conal', value: convertUnderscoreToTitle(retrievalData?.endocervical_conal) },
    { label: 'Uterus Cervical Length (mm)', value: retrievalData?.uterus_cervical_length },
    { label: 'Cavity', value: convertUnderscoreToTitle(retrievalData?.cavity) },
    { label: 'Endometrium', value: convertUnderscoreToTitle(retrievalData?.endometrium) },
    { label: 'Right Ostia', value: retrievalData?.right_ostia },
    { label: 'Left Ostia', value: retrievalData?.left_ostia },
    { label: 'EB-HPE', value: retrievalData?.eb_hpe },
    { label: 'Other Details', value: retrievalData?.other_details },
    { label: 'Remark', value: retrievalData?.remarks },
    { label: 'Done By', value: retrievalData?.done_by_name },
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3 }}>
        <Grid container spacing={4} mb={3}>
          {fields.map((field, index) => (
            field.value && (
              <Grid key={index} size={3} sx={{ textAlign: 'left' }}>
                <Typography variant="body1" sx={sectionTitleStyle}>
                  {field.label}
                </Typography>
                <Typography variant="body2" sx={infoTextStyle}>
                  {field.value}
                </Typography>
              </Grid>
            )
          ))}
        </Grid> 

        {/* Image Gallery */}
        {retrievalData?.images && retrievalData.images.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="body1" sx={sectionTitleStyle}>
              Images
            </Typography>
            <Grid container spacing={2}>
              {retrievalData.images.map((img, idx) => (
                <Grid key={img.id}>
                  <img
                    src={img.image}
                    alt={`Hysteroscopy ${idx + 1}`}
                    style={{ width: '40px', height: '40px', cursor: 'pointer', borderRadius: 25 }}
                    onClick={() => handleOpenDialog(idx)}
                  />
                </Grid>
              ))}

            </Grid>
          </Box>
        )}


        <ImageLightbox
          open={openImageDialog}
          images={selectedImages}
          initialIndex={selectedImageIndex}  // Pass the index here
          onClose={handleCloseDialog}
          label="Preview"
        />


        <Box className={styles.customButtonDiv}>
          <button
            type="button"
            className={styles.resetButton}
            onClick={onBack}
          >
            Back to List
          </button>

          <button
            type="button"
            className={styles.customButton}
            onClick={onEdit}
          >
            <span style={{ fontWeight: 'bold' }}>
              Edit
            </span>
          </button>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default HysteroscopyViewDetails;
